import React from 'react';
import classnames from 'classnames';
import { getHeight } from './helpers';
import Text from '../text/Text';
import { OptionType, PositionType, SelectionType } from './types';
import Checkbox from '../checkbox/Checkbox';
import { hasAlready } from '../../utils/arrayOperation';

interface Props {
  portal: boolean;
  dropUp: boolean;
  effectiveOptions: OptionType[];
  position: PositionType;
  size?: 'sm' | 'md' | 'lg' | 'auto';
  zIndexValue: number;
  isMulti: boolean;
  placeholder: string;
  emptyMessage: string;
  scroll?: number;
  handleClickOnOption: (currentOption: OptionType) => void;
  inputWrapperRef: React.MutableRefObject<HTMLDivElement>;
  dropdownRef: React.MutableRefObject<HTMLDivElement>;
  isCheckable?: boolean;
  handleSelectAll?: () => void;
  selectedValues: SelectionType;
  isRequiredSelection: boolean;
  numberOfSelection: number;
  testId?: string;
}

const SelectMenu = ({
  portal,
  dropUp,
  effectiveOptions,
  inputWrapperRef,
  position,
  size,
  zIndexValue,
  dropdownRef,
  isMulti,
  placeholder,
  emptyMessage,
  handleClickOnOption,
  // scroll,
  isCheckable,
  handleSelectAll,
  selectedValues,
  isRequiredSelection,
  numberOfSelection,
  testId,
}: Props): string | JSX.Element | JSX.Element[] => {
  let top = 0,
    left = 0;
  if (portal) {
    const isDropUp = dropUp
      ? getHeight(effectiveOptions?.length || 0) +
        inputWrapperRef.current.clientHeight +
        10
      : 0;
    top = position.y + position.height + window.scrollY - isDropUp;
    left = position.x;
  }

  const getMenuItem = (option): JSX.Element => {
    return (
      <div
        className={classnames('px-4 pointer select-menu-item', {
          'py-5': !isCheckable,
          'bg-primary-light': isMulti
            ? hasAlready(selectedValues as [], 'value', option.value)
            : (selectedValues as OptionType)?.value === option.value,
        })}
        {...(!isCheckable && {
          onClick: () => handleClickOnOption(option),
          ...(testId && { 'data-testid': testId + `-${option.value}` }),
        })}
      >
        {option.customComponent ? (
          option.customComponent
        ) : isCheckable && isMulti ? (
          <Checkbox
            label={option.label}
            id={option.value}
            name={option.label}
            value={option.value}
            checked={hasAlready(selectedValues as [], 'value', option.value)}
            onChange={() => handleClickOnOption(option)}
            className="w-100 py-5"
            controlling
            data-testid={testId + `-${option.value}`}
          />
        ) : (
          <span>{option.label}</span>
        )}
      </div>
    );
  };

  return (
    <div
      className={classnames('shadow-xl position-absolute mt-1 p-3 z-1', {
        'select-menu': true,
        [`select-menu-${size}`]: size,
        [`z-${zIndexValue}`]: zIndexValue,
      })}
      {...(portal && {
        style: {
          top: isNaN(top) ? 0 : top,
          left: isNaN(left) ? 0 : left,
          width: position.width,
        },
        ref: dropdownRef,
      })}
    >
      {effectiveOptions?.length && !isMulti && !isRequiredSelection ? (
        <React.Fragment key="select">
          {getMenuItem({
            label: placeholder,
            value: '',
          })}
        </React.Fragment>
      ) : null}

      {!numberOfSelection &&
      effectiveOptions?.length &&
      isMulti &&
      isCheckable ? (
        <Checkbox
          label="Select all"
          id="select-all"
          name="select-all"
          value="select-all"
          onChange={handleSelectAll}
          className="w-100 py-5"
          checked={effectiveOptions?.length === (selectedValues as [])?.length}
          controlling
        />
      ) : null}

      {effectiveOptions?.length ? (
        effectiveOptions?.map((option, index) => (
          <React.Fragment key={index}>{getMenuItem(option)}</React.Fragment>
        ))
      ) : (
        <div className="px-4 py-5 pointer select-menu-item">
          <Text>{emptyMessage || 'NO DATA'}</Text>
        </div>
      )}
    </div>
  );
};

export default SelectMenu;
